  // cSpell:ignore headlessui
  import React, { useState, useEffect, useRef, useContext } from 'react';
  import { useForm } from '@refinedev/react-hook-form';
  import { useCreate, HttpError, useOne, BaseRecord } from '@refinedev/core';
  import { Button } from '../../components/buttons/button';
  import { Input } from '../../components/input';
  import { Textarea } from '../../components/textarea';
  import { Select } from '../../components/select';
  import { Badge } from '../../components/badge';
  import { semanticColors } from '../../theme';
  import { CreateNewShift } from '../../shared/types/shift';
  import useApiService from '../../hooks/useApiService';
  import { FacilityContext } from '../../components/layout';
  import dayjs from 'dayjs';
  import {
    XMarkIcon,
    CalendarDaysIcon,
    ClockIcon,
    UserIcon,
    PlusIcon,
    TrashIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckIcon,
  } from '@heroicons/react/24/outline';

  interface Position {
    position_id: string;
    position_name: string;
  }

  interface VhUserPeople {
    firstname: string;
    lastname: string;
    email?: string;
    phone?: string;
  }

  interface VhUser {
    id?: string | number;
    user_id?: string | number;
    people: VhUserPeople;
  }

  interface MeDataWrapper extends BaseRecord {
    id: string | number;
    data: [any, VhUser]; // First element is facility, second is user
  }

  interface PostShiftModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (shift: Partial<CreateNewShift>) => void;
    isSubmitting?: boolean;
  }

  const PostShiftModal: React.FC<PostShiftModalProps> = ({ 
    open, 
    onClose, 
    onSubmit, 
    isSubmitting
  }) => {
    const api = useApiService();
    const { facility, user } = useContext(FacilityContext);
    
    // Fallback: Fetch user data directly if context doesn't have it
    const { data: fallbackMeData } = useOne<MeDataWrapper>({
      resource: "people",
      id: "me",
      queryOptions: {
        enabled: !facility?.facility_id || !user?.user_id, // Only fetch if context is missing data
      }
    });
    
    // Use fallback data if context data is missing
    const effectiveFacility = facility || fallbackMeData?.data?.[0];
    const effectiveUser = user || fallbackMeData?.data?.[1];
    const {
      register,
      handleSubmit,
      setValue,
      watch,
      formState: { errors, isValid, isDirty },
      trigger,
      reset,
      clearErrors,
      setError,
    } = useForm<CreateNewShift, HttpError, CreateNewShift>({
      defaultValues: {
        description: '',
        instructions: '',
        break_time: 30,
        start_time: '',
        end_time: '',
        dates: [],
        position_id: '',
        number_of_openings: 1,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        facility_id: '',
        created_by: '',
      },
    });

    const [positions, setPositions] = useState<Position[]>([]);
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'));
    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
    const [showCustomTime, setShowCustomTime] = useState<boolean>(false);
    const [activeStep, setActiveStep] = useState(1);

    // On-blur validator for number_of_openings to enforce max 7 and sanitize commas
    const handleOpeningsBlur = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const raw = e.target.value ?? '';
      const cleaned = raw.replace(/[^0-9]/g, '');
      const parsed = cleaned ? parseInt(cleaned, 10) : NaN;

      // Sync sanitized value back to RHF state
      if (cleaned !== raw) {
        setValue('number_of_openings', cleaned ? parseInt(cleaned, 10) : ('' as unknown as number), { shouldValidate: true, shouldDirty: true });
      }

      if (!isNaN(parsed) && parsed > 7) {
        setError('number_of_openings', { type: 'max', message: 'Maximum 7 openings allowed' }, { shouldFocus: false });
      } else if (!isNaN(parsed) && parsed < 1) {
        setError('number_of_openings', { type: 'min', message: 'Minimum 1 opening' }, { shouldFocus: false });
      } else if (cleaned === '') {
        // Treat empty as invalid until user provides a value
        setError('number_of_openings', { type: 'required', message: 'Number of openings is required' }, { shouldFocus: false });
      } else {
        clearErrors('number_of_openings');
      }
    };

    // Facility details state for auto-loading
    const [facilityDetails, setFacilityDetails] = useState<{
      instructions: string | null;
      description: string | null;
    } | null>(null);
    const [facilityDetailsLoading, setFacilityDetailsLoading] = useState(false);

    const [startHour, setStartHour] = useState('');
    const [startMinute, setStartMinute] = useState('00');
    const [startPeriod, setStartPeriod] = useState<'AM' | 'PM'>('AM');
    const [endHour, setEndHour] = useState('');
    const [endMinute, setEndMinute] = useState('00');
    const [endPeriod, setEndPeriod] = useState<'AM' | 'PM'>('AM');

    const startMinuteRef = useRef<HTMLInputElement>(null);
    const endHourRef = useRef<HTMLInputElement>(null);
    const endMinuteRef = useRef<HTMLInputElement>(null);

    const fetchPositions = async () => {
      if (!facility?.facility_id) {
        return;
      }
      
      setPositionsLoading(true);
      try {
        const response = await api.get<Position[]>(`/positions/find-facility-positions?facilityId=${facility.facility_id}`);
        setPositions(response.data);
      } catch (error) {
        setPositions([]);
      } finally {
        setPositionsLoading(false);
      }
    };

    // Fetch facility details for auto-loading instructions and description
    const fetchFacilityDetails = async () => {
      if (!facility?.facility_id) {
        return;
      }
      
      setFacilityDetailsLoading(true);
      try {
        const response = await api.get<{
          instructions: string | null;
          description: string | null;
        }>(`/facilities/facility-details?facilityId=${facility.facility_id}`);
        setFacilityDetails(response.data);
      } catch (error) {
        // Facility details don't exist yet, that's okay
        setFacilityDetails(null);
      } finally {
        setFacilityDetailsLoading(false);
      }
    };

    // Fetch facility-specific positions and details
    useEffect(() => {
      if (open && facility?.facility_id) {
        fetchPositions();
        fetchFacilityDetails();
      }
    }, [open, facility?.facility_id]); // api and fetchPositions are stable

    // Set facility_id and created_by when data is available
    useEffect(() => {
      if (effectiveFacility?.facility_id && effectiveUser?.user_id) {
        setValue('facility_id', effectiveFacility.facility_id, { shouldValidate: true, shouldDirty: true });
        setValue('created_by', effectiveUser.user_id.toString(), { shouldValidate: true, shouldDirty: true });
      }
    }, [effectiveFacility?.facility_id, effectiveUser, setValue, facility?.facility_id, user?.user_id, fallbackMeData?.data]);

    // Ensure hidden fields are set when modal opens
    useEffect(() => {
      if (open) {
        // Set initial values immediately
        setValue('facility_id', effectiveFacility?.facility_id || '', { shouldValidate: true, shouldDirty: true });
        setValue('created_by', effectiveUser?.user_id?.toString() || '', { shouldValidate: true, shouldDirty: true });
        
        // Use a small delay to ensure the form is fully initialized
        const timer = setTimeout(() => {
          // If we have the real data, set it again
          if (effectiveFacility?.facility_id && effectiveUser?.user_id) {
            setValue('facility_id', effectiveFacility.facility_id, { shouldValidate: true, shouldDirty: true });
            setValue('created_by', effectiveUser.user_id.toString(), { shouldValidate: true, shouldDirty: true });
          }
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }, [open, effectiveFacility?.facility_id, effectiveUser?.user_id, setValue]);

    // Auto-load facility instructions and description when available
    useEffect(() => {
      if (facilityDetails && open) {
        if (facilityDetails.description) {
          setValue('description', facilityDetails.description, { shouldValidate: true, shouldDirty: true });
        }
        if (facilityDetails.instructions) {
          setValue('instructions', facilityDetails.instructions, { shouldValidate: true, shouldDirty: true });
        }
      }
    }, [facilityDetails, open, setValue]);

    useEffect(() => {
      register('start_time', { required: showCustomTime });
      register('end_time', { required: showCustomTime });
      // Register the hidden fields that are automatically populated
      register('facility_id');
      register('created_by');
    }, [register, showCustomTime]);

    const to24HourFormat = (hour: string, minute: string, period: 'AM' | 'PM') => {
      let h = parseInt(hour, 10);
      const m = parseInt(minute, 10);
      if (period === 'PM' && h !== 12) h += 12;
      if (period === 'AM' && h === 12) h = 0;
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };



    useEffect(() => {
      if (startHour && startMinute && startPeriod) {
        const h = parseInt(startHour, 10);
        const m = parseInt(startMinute, 10);
        if (h >= 1 && h <= 12 && m >= 0 && m <= 59) {
          // Check if this custom time is in the past for today
          const isInPast = isCustomTimeInPast();
          if (!isInPast) {
            const time24 = to24HourFormat(startHour, startMinute, startPeriod);
            setValue('start_time', time24, { shouldValidate: true, shouldDirty: true });
          } else {
            setValue('start_time', '', { shouldValidate: true, shouldDirty: true });
          }
        } else {
          setValue('start_time', '', { shouldValidate: true, shouldDirty: true });
        }
      } else {
        setValue('start_time', '', { shouldValidate: true, shouldDirty: true });
      }
    }, [startHour, startMinute, startPeriod, setValue, selectedDates]);

    useEffect(() => {
      if (endHour && endMinute && endPeriod) {
        const h = parseInt(endHour, 10);
        const m = parseInt(endMinute, 10);
        if (h >= 1 && h <= 12 && m >= 0 && m <= 59) {
          const time24 = to24HourFormat(endHour, endMinute, endPeriod);
          setValue('end_time', time24, { shouldValidate: true, shouldDirty: true });
        } else {
          setValue('end_time', '', { shouldValidate: true, shouldDirty: true });
        }
      } else {
        setValue('end_time', '', { shouldValidate: true, shouldDirty: true });
      }
    }, [endHour, endMinute, endPeriod, setValue]);

    const watchedPositionId = watch('position_id');

    const timeSlots = [
      '12:30 AM - 06:30 AM', '02:30 AM - 06:30 AM', '05:00 AM - 01:30 PM', '06:00 AM - 02:00 PM',
      '06:30 AM - 10:30 AM', '06:30 AM - 12:30 PM', '06:30 AM - 02:30 PM', '06:00 AM - 06:30 PM',
      '07:00 AM - 11:00 AM', '07:00 AM - 03:00 PM', '07:00 AM - 07:00 PM', '10:30 AM - 06:30 PM',
      '01:00 PM - 09:30 PM', '02:00 PM - 10:00 PM', '02:30 PM - 06:30 PM', '03:00 PM - 07:00 PM',
      '03:00 PM - 11:00 PM', '06:30 PM - 12:30 AM', '06:30 PM - 02:30 AM', '06:30 PM - 06:30 AM',
      '06:30 PM - 10:30 PM', '07:00 PM - 07:00 AM', '07:00 PM - 11:00 PM', '10:00 PM - 06:00 AM',
      '10:30 PM - 06:30 AM', '11:00 PM - 07:00 AM',
    ];

    const breakTimeOptions = [
      { value: 0, label: '0 minutes' }, { value: 15, label: '15 minutes' }, { value: 30, label: '30 minutes' },
      { value: 45, label: '45 minutes' }, { value: 60, label: '1 hour' },
    ];

    const handleDateChange = (date: string): void => {
      // Prevent selecting past dates
      const today = dayjs().format('YYYY-MM-DD');
      if (dayjs(date).isBefore(today, 'day')) {
        return; // Don't allow selection of past dates
      }
      
      const newDates = selectedDates.includes(date)
        ? selectedDates.filter((d) => d !== date)
        : [...selectedDates, date];
      setSelectedDates(newDates);
      setValue('dates', newDates, { shouldValidate: true, shouldDirty: true });
    };

    const handleTimeInputChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      setter: React.Dispatch<React.SetStateAction<string>>,
      maxLength: number,
      nextFieldRef?: React.RefObject<HTMLInputElement>,
      isHour = false,
      isMinute = false
    ) => {
      const value = e.target.value.replace(/[^0-9]/g, '');
      
      // Validate hour input (1-12)
      if (isHour && value && (parseInt(value, 10) < 1 || parseInt(value, 10) > 12)) {
        return;
      }
      
      // Validate minute input (0-59)
      if (isMinute && value && (parseInt(value, 10) < 0 || parseInt(value, 10) > 59)) {
        return;
      }
      
      if (value.length <= maxLength) {
        setter(value);
        if (value.length === maxLength && nextFieldRef?.current) {
          nextFieldRef.current.focus();
        }
      }
    };

    const handleTimeSlotSelect = (slot: string): void => {
      // Prevent selecting past time slots for today
      if (slot !== 'Custom Time' && isTimeSlotInPast(slot)) {
        return; // Don't allow selection of past time slots
      }
      
      setSelectedTimeSlot(slot);
      setStartHour(''); setStartMinute('00'); setStartPeriod('AM');
      setEndHour(''); setEndMinute('00'); setEndPeriod('AM');

      if (slot === 'Custom Time') {
        setShowCustomTime(true);
        setValue('start_time', '', { shouldValidate: true });
        setValue('end_time', '', { shouldValidate: true });
      } else {
        setShowCustomTime(false);
        const [start, end] = slot.split(' - ').map((time) => {
          const [t, period] = time.trim().split(' ');
          const [h, m] = t.split(':');
          return to24HourFormat(h, m, period as 'AM' | 'PM');
        });
        setValue('start_time', start, { shouldValidate: true, shouldDirty: true });
        setValue('end_time', end, { shouldValidate: true, shouldDirty: true });
      }
      trigger(['start_time', 'end_time']);
    };

    const handlePreviousMonth = (): void => {
      setCurrentMonth(currentMonth.subtract(1, 'month'));
    };

    const getTimezoneAbbr = () => {
      const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'long', hour: 'numeric', minute: 'numeric' });
      const parts = formatter.formatToParts(new Date());
      return parts.find(part => part.type === 'timeZoneName')?.value || 'Unknown';
    };

    // Function to get formatted time display for summary
    const getFormattedTimeDisplay = () => {
      if (!selectedTimeSlot) {
        return 'Not set';
      }
      
      if (selectedTimeSlot === 'Custom Time') {
        // For custom time, format the actual selected time
        if (startHour && startMinute && endHour && endMinute) {
          const formattedStartTime = `${startHour.padStart(2, '0')}:${startMinute.padStart(2, '0')} ${startPeriod}`;
          const formattedEndTime = `${endHour.padStart(2, '0')}:${endMinute.padStart(2, '0')} ${endPeriod}`;
          return `${formattedStartTime} - ${formattedEndTime}`;
        } else {
          return 'Custom Time (incomplete)';
        }
      }
      
      // For predefined time slots, return as is
      return selectedTimeSlot;
    };

    // Function to check if a time slot is in the past for today
    const isTimeSlotInPast = (timeSlot: string): boolean => {
      const today = dayjs().format('YYYY-MM-DD');
      const hasToday = selectedDates.some(date => date === today);
      
      if (!hasToday) {
        return false; // If today is not selected, no time restrictions
      }
      
      try {
        // Parse the time slot (e.g., "07:00 AM - 03:00 PM")
        const [startTimeStr] = timeSlot.split(' - ');
        const [timePart, period] = startTimeStr.trim().split(' ');
        const [hours, minutes] = timePart.split(':').map(Number);
        
        let hour24 = hours;
        if (period === 'PM' && hours !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hours === 12) {
          hour24 = 0;
        }
        
        const shiftStartTime = dayjs().hour(hour24).minute(minutes).second(0);
        const now = dayjs();
        
        return shiftStartTime.isBefore(now);
      } catch (error) {
        return false;
      }
    };

    // Function to check if custom time is in the past for today
    const isCustomTimeInPast = React.useCallback((): boolean => {
      const today = dayjs().format('YYYY-MM-DD');
      const hasToday = selectedDates.some(date => date === today);
      
      if (!hasToday || !startHour || !startMinute) {
        return false;
      }
      
      try {
        const h = parseInt(startHour, 10);
        const m = parseInt(startMinute, 10);
        
        if (h < 1 || h > 12 || m < 0 || m > 59) {
          return false;
        }
        
        let hour24 = h;
        if (startPeriod === 'PM' && h !== 12) {
          hour24 += 12;
        } else if (startPeriod === 'AM' && h === 12) {
          hour24 = 0;
        }
        
        const shiftStartTime = dayjs().hour(hour24).minute(m).second(0);
        const now = dayjs();
        
        return shiftStartTime.isBefore(now);
      } catch (error) {
        return false;
      }
    }, [selectedDates, startHour, startMinute, startPeriod]);

    // Function to validate shift duration (minimum 60 minutes, maximum 16 hours)
    const validateShiftDuration = React.useCallback((): { isValid: boolean; message: string } => {
      if (!startHour || !startMinute || !endHour || !endMinute) {
        return { isValid: false, message: 'Please complete both start and end times' };
      }

      try {
        // Convert to 24-hour format for calculation
        const startTime = to24HourFormat(startHour, startMinute, startPeriod);
        const endTime = to24HourFormat(endHour, endMinute, endPeriod);
        
        // Parse times
        const start = dayjs(`2000-01-01 ${startTime}`);
        const end = dayjs(`2000-01-01 ${endTime}`);
        
        // Handle overnight shifts (end time is next day)
        let duration = end.diff(start, 'minute');
        if (duration <= 0) {
          duration += 24 * 60; // Add 24 hours if end time is before start time
        }
        
        // Check minimum duration (60 minutes)
        if (duration < 60) {
          return { 
            isValid: false, 
            message: 'Shift duration must be at least 60 minutes' 
          };
        }
        
        // Check maximum duration (16 hours = 960 minutes)
        if (duration > 960) {
          return { 
            isValid: false, 
            message: 'Shift duration cannot exceed 16 hours' 
          };
        }
        
        return { isValid: true, message: '' };
      } catch (error) {
        return { isValid: false, message: 'Invalid time format' };
      }
    }, [startHour, startMinute, startPeriod, endHour, endMinute, endPeriod]);

    // Helper function to check if shift duration is valid (returns boolean only)
    const isShiftDurationValid = React.useCallback((): boolean => {
      if (!startHour || !startMinute || !endHour || !endMinute) {
        return false;
      }

      try {
        // Convert to 24-hour format for calculation
        const startTime = to24HourFormat(startHour, startMinute, startPeriod);
        const endTime = to24HourFormat(endHour, endMinute, endPeriod);
        
        // Parse times
        const start = dayjs(`2000-01-01 ${startTime}`);
        const end = dayjs(`2000-01-01 ${endTime}`);
        
        // Handle overnight shifts (end time is next day)
        let duration = end.diff(start, 'minute');
        if (duration <= 0) {
          duration += 24 * 60; // Add 24 hours if end time is before start time
        }
        
        // Check minimum duration (60 minutes) and maximum duration (16 hours = 960 minutes)
        return duration >= 60 && duration <= 960;
      } catch (error) {
        return false;
      }
    }, [startHour, startMinute, startPeriod, endHour, endMinute, endPeriod]);

    const handleNextMonth = (): void => {
      setCurrentMonth(currentMonth.add(1, 'month'));
    };

    const renderCalendar = (month: dayjs.Dayjs) => {
      const daysInMonth = month.daysInMonth();
      const firstDayOfMonth = month.startOf('month').day(); 
      const days: JSX.Element[] = [];
    
      
      const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
      for (let i = 0; i < offset; i++) {
        days.push(<div key={`empty-${i}`} className="text-center text-gray-400">-</div>);
      }
    
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = month.date(day).format('YYYY-MM-DD');
        const isSelected = selectedDates.includes(dateStr);
        const isToday = dayjs().format('YYYY-MM-DD') === dateStr;
        const isPastDate = dayjs(dateStr).isBefore(dayjs(), 'day');
    
        days.push(
          <div
            key={dateStr}
            className={`rounded-full py-0.5 text-center text-xs transition-all duration-200 ${
              isPastDate
                ? 'text-gray-300 cursor-not-allowed opacity-50'
                : isSelected
                  ? 'bg-[#4A6FA5] text-white shadow-sm cursor-pointer'
                  : isToday
                    ? 'border border-[#F5B957] text-[#2A333C] cursor-pointer'
                    : 'text-[#677D94] hover:bg-[#F8FAFC] cursor-pointer'
            }`}
            onClick={() => !isPastDate && handleDateChange(dateStr)}
            role="button"
            aria-label={`${isPastDate ? 'Past date (disabled)' : 'Select'} ${month.date(day).format('MMMM D, YYYY')}`}
            title={isPastDate ? 'Cannot select past dates' : `Select ${month.date(day).format('MMMM D, YYYY')}`}
          >
            {day}
          </div>
        );
      }
    
      return days;
    };

    const handleClose = (): void => {
      if (isDirty) {
        if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          // Clear form values
          reset();
          
          // Clear all state variables
          setSelectedDates([]); 
          setSelectedTimeSlot(null); 
          setShowCustomTime(false); 
          setStartHour(''); 
          setStartMinute('00'); 
          setStartPeriod('AM');
          setEndHour(''); 
          setEndMinute('00'); 
          setEndPeriod('AM');
          setActiveStep(1);
          setCurrentMonth(dayjs().startOf('month'));
          onClose();
        }
      } else {
        // Clear form values
        reset();
        
        // Clear all state variables
        setSelectedDates([]); 
        setSelectedTimeSlot(null); 
        setShowCustomTime(false); 
        setStartHour(''); 
        setStartMinute(''); 
        setStartPeriod('AM');
        setEndHour(''); 
        setEndMinute(''); 
        setEndPeriod('AM');
        setActiveStep(1);
        setCurrentMonth(dayjs().startOf('month'));
        onClose();
      }
    };

    const handleRemoveShift = (): void => {
      // Clear form values
      reset();
      
      // Clear all state variables
      setSelectedDates([]); 
      setSelectedTimeSlot(null); 
      setShowCustomTime(false);
      setStartHour(''); 
      setStartMinute('00'); 
      setStartPeriod('AM');
      setEndHour(''); 
      setEndMinute('00'); 
      setEndPeriod('AM');
      
      // Reset form fields to default values
      setValue('description', '');
      setValue('instructions', '');
      setValue('break_time', 30);
      setValue('start_time', '');
      setValue('end_time', '');
      setValue('dates', []);
      setValue('position_id', '');
      setValue('number_of_openings', 1);
      
      // Reset to first step
      setActiveStep(1);
      
      // Reset current month to current month
      setCurrentMonth(dayjs().startOf('month'));
    };

    const handleClearConfiguration = (): void => {
      // Only clear Shift Configuration and Shift Information cards
      // Preserve date and time selections
      
      // Clear form fields for configuration and information
      setValue('description', '');
      setValue('instructions', '');
      setValue('break_time', 30);
      setValue('position_id', '');
      setValue('number_of_openings', 1);
      
      // Clear errors for these fields
      clearErrors(['description', 'instructions', 'break_time', 'position_id', 'number_of_openings']);
    };

    useEffect(() => {
      if (open) {
        // Clear form values
        reset(); 
        
        // Clear all state variables
        setSelectedDates([]); 
        setSelectedTimeSlot(null); 
        setShowCustomTime(false);
        setStartHour(''); 
        setStartMinute('00'); 
        setStartPeriod('AM');
        setEndHour(''); 
        setEndMinute('00'); 
        setEndPeriod('AM');
        setCurrentMonth(dayjs().startOf('month'));
        setActiveStep(1);
      }
    }, [open, reset]);

    const watchedDates = watch('dates');
    const watchedStartTime = watch('start_time');
    const watchedEndTime = watch('end_time');
    const watchedFacilityId = watch('facility_id');
    const watchedCreatedBy = watch('created_by');
    // Check if we have the required hidden fields
    const hasHiddenFields = watchedFacilityId && watchedCreatedBy;
    
    // Form is submittable when all required fields are filled and hidden fields are set
    const isFormSubmittable = !!(
      watchedDates && 
      watchedDates.length > 0 && 
      watchedStartTime && 
      watchedEndTime && 
      watchedPositionId
    ) && isValid && hasHiddenFields;

    const getDateRangeMessage = () => {
      if (selectedDates.length === 0) return null;
      const sortedDates = [...selectedDates].sort();
      const startDate = sortedDates[0];
      const endDate = sortedDates[sortedDates.length - 1];
      const today = dayjs().format('YYYY-MM-DD');

      const startTime = watchedStartTime ? dayjs(`${startDate} ${watchedStartTime}`, 'YYYY-MM-DD HH:mm') : null;
      const endTime = watchedEndTime ? dayjs(`${startDate} ${watchedEndTime}`, 'YYYY-MM-DD HH:mm') : null;

      if (startTime && endTime && endTime.isBefore(startTime)) {
        if (selectedDates.includes(today)) {
          return 'Your shift is ending tomorrow';
        }
        return `Your shift will end the next day`;
      }

      return `You have selected the time from ${startDate} to ${endDate}`;
    };

    if (!open) return null;

    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm">
        <div className="w-full max-w-xl h-full overflow-y-auto bg-white shadow-2xl sm:max-w-2xl lg:max-w-xl">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#CBD5E1] bg-white px-3 py-2 sm:px-4 sm:py-3 lg:px-3 lg:py-2">
            <div className="flex items-center space-x-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#4A6FA5]">
                <CalendarDaysIcon className="h-4 w-4 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#2A333C]">Create Shift</h2>
                <p className="text-xs text-[#677D94]">Schedule for your facility</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="rounded-full p-1.5 text-[#677D94] transition-colors hover:bg-[#F8FAFC] hover:text-[#2A333C]"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="border-b border-[#CBD5E1] bg-[#F8FAFC] px-3 py-1.5 sm:px-4 sm:py-2 lg:px-3 lg:py-1.5">
            <div className="flex items-center justify-center space-x-3">
              {[
                { step: 1, title: 'Dates', icon: CalendarDaysIcon },
                { step: 2, title: 'Time', icon: ClockIcon },
                { step: 3, title: 'Details', icon: UserIcon }
              ].map(({ step, title, icon: Icon }) => (
                <div key={step} className="flex items-center space-x-1">
                  <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                    activeStep >= step 
                      ? 'bg-[#4A6FA5] text-white' 
                      : 'bg-[#CBD5E1] text-[#677D94]'
                  }`}>
                    {activeStep > step ? '✓' : step}
                  </div>
                  <span className={`text-xs font-medium ${
                    activeStep >= step ? 'text-[#2A333C]' : 'text-[#677D94]'
                  }`}>
                    {title}
                  </span>
                  {step < 3 && (
                    <div className={`h-px w-4 ${
                      activeStep > step ? 'bg-[#4A6FA5]' : 'bg-[#CBD5E1]'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit((data) => {
            // Final safety check - ensure hidden fields are set before submission
            if (!data.facility_id && effectiveFacility?.facility_id) {
              setValue('facility_id', effectiveFacility.facility_id, { shouldValidate: true, shouldDirty: true });
              data.facility_id = effectiveFacility.facility_id;
            }
            if (!data.created_by && effectiveUser?.user_id) {
              setValue('created_by', effectiveUser.user_id.toString(), { shouldValidate: true, shouldDirty: true });
              data.created_by = effectiveUser.user_id.toString();
            }
            
            // Validate number of openings (inline error)
            if (typeof data.number_of_openings !== 'number' || Number.isNaN(data.number_of_openings)) {
              setError('number_of_openings', { type: 'required', message: 'Number of openings is required' }, { shouldFocus: true });
              return;
            }
            if (data.number_of_openings > 7) {
              setError('number_of_openings', { type: 'max', message: 'Maximum 7 openings allowed' }, { shouldFocus: true });
              return;
            }
            if (data.number_of_openings < 1) {
              setError('number_of_openings', { type: 'min', message: 'Minimum 1 opening' }, { shouldFocus: true });
              return;
            }
            
            onSubmit(data);
          })} className="p-4">
            {/* Step 1: Date Selection */}
            {activeStep === 1 && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-base font-semibold text-[#2A333C]">Select Shift Dates</h3>
                  <p className="mt-1 text-xs text-[#677D94]">Choose the dates when this shift will be active</p>
                </div>
                
                <div className="rounded-lg border border-[#CBD5E1] bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handlePreviousMonth}
                      className="rounded-full p-1.5 text-[#677D94] transition-colors hover:bg-[#F8FAFC] hover:text-[#2A333C]"
                    >
                      <ChevronLeftIcon className="h-3 w-3" />
                    </button>
                    <h4 className="text-base font-semibold text-[#2A333C]">{currentMonth.format('MMMM YYYY')}</h4>
                    <button
                      type="button"
                      onClick={handleNextMonth}
                      className="rounded-full p-1.5 text-[#677D94] transition-colors hover:bg-[#F8FAFC] hover:text-[#2A333C]"
                    >
                      <ChevronRightIcon className="h-3 w-3" />
                    </button>
                  </div>
                  
               <div className="grid grid-cols-7 gap-0.5 text-center text-xs font-medium text-[#677D94]">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                   <div key={`day-${index}`} className="py-1">{day}</div>
                      ))}
                      {renderCalendar(currentMonth)}
                    </div>
                  
                  {selectedDates.length > 0 && (
                    <div className="mt-3 rounded-lg bg-[#E0EDE2] p-2">
                      <p className="text-xs font-medium text-[#3A7D44]">
                        Selected: {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''}
                      </p>
                      <p className="text-xs text-[#2E6B3A]">
                        {selectedDates.sort().join(', ')}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-lg border border-[#CBD5E1] px-3 py-1.5 text-xs font-medium text-[#677D94] transition-colors hover:bg-[#F8FAFC]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveStep(2)}
                    disabled={selectedDates.length === 0}
                    className="rounded-lg bg-[#4A6FA5] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#3A5F7D] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Time Selection */}
            {activeStep === 2 && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-base font-semibold text-[#2A333C]">Select Shift Time</h3>
                  <p className="mt-1 text-xs text-[#677D94]">Choose the time slot for your shift</p>
                </div>
                
                <div className="rounded-lg border border-[#CBD5E1] bg-white p-4 shadow-sm">
                  <div className="mb-3">
                    <h4 className="mb-2 text-xs font-medium text-[#2A333C]">
                      Shift Time – {getTimezoneAbbr()}
                    </h4>
                    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4">
                      {timeSlots.map((slot) => {
                        const isPastTime = isTimeSlotInPast(slot);
                        return (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => !isPastTime && handleTimeSlotSelect(slot)}
                            disabled={isPastTime}
                            className={`rounded-lg border px-2 py-1.5 text-xs font-medium transition-all duration-200 ${
                              isPastTime
                                ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                                : selectedTimeSlot === slot 
                                  ? 'border-[#4A6FA5] bg-[#E0E8ED] text-[#4A6FA5] shadow-sm' 
                                  : 'border-[#CBD5E1] text-[#677D94] hover:border-[#94A3B8] hover:bg-[#F8FAFC]'
                            }`}
                            title={isPastTime ? 'This time has already passed today' : slot}
                          >
                            {slot}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => handleTimeSlotSelect('Custom Time')}
                        className={`rounded-lg border px-2 py-1.5 text-xs font-medium transition-all duration-200 ${
                          selectedTimeSlot === 'Custom Time' 
                            ? 'border-[#4A6FA5] bg-[#E0E8ED] text-[#4A6FA5] shadow-sm' 
                            : 'border-[#CBD5E1] text-[#677D94] hover:border-[#94A3B8] hover:bg-[#F8FAFC]'
                        }`}
                      >
                        Custom Time
                      </button>
                    </div>
                  </div>
                  
                                  {showCustomTime && (
                    <div className="mt-3 rounded-lg border border-[#CBD5E1] bg-[#F8FAFC] p-3">
                      <h5 className="mb-2 text-xs font-medium text-[#2A333C]">Custom Time</h5>
                      <div className="mb-2 text-xs text-amber-600 font-medium">
                        ⏰ Shift duration: Minimum 60 minutes, Maximum 16 hours
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                              <div>
                          <label className="mb-1 block text-xs font-medium text-[#2A333C]">Start Time</label>
                          <div className="flex items-center space-x-1">
                            <input
                              type="text"
                              placeholder="HH"
                              value={startHour}
                              onChange={(e) => handleTimeInputChange(e, setStartHour, 2, startMinuteRef, true)}
                              className="w-12 rounded-lg border border-[#CBD5E1] px-2 py-1 text-center text-xs focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            />
                            <span className="text-[#677D94] text-xs">:</span>
                            <input
                              type="text"
                              placeholder="MM"
                              ref={startMinuteRef}
                              value={startMinute}
                              onChange={(e) => handleTimeInputChange(e, setStartMinute, 2, endHourRef, false, true)}
                              className="w-12 rounded-lg border border-[#CBD5E1] px-2 py-1 text-center text-xs focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            />
                            <select
                              value={startPeriod}
                              onChange={(e) => setStartPeriod(e.target.value as 'AM' | 'PM')}
                              className="rounded-lg border border-[#CBD5E1] px-2 py-1 text-xs focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            >
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                          </div>
                        </div>
                                              <div>
                          <label className="mb-1 block text-xs font-medium text-[#2A333C]">End Time</label>
                          <div className="flex items-center space-x-1">
                            <input
                              type="text"
                              placeholder="HH"
                              ref={endHourRef}
                              value={endHour}
                              onChange={(e) => handleTimeInputChange(e, setEndHour, 2, endMinuteRef, true)}
                              className="w-12 rounded-lg border border-[#CBD5E1] px-2 py-1 text-center text-xs focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            />
                            <span className="text-[#677D94] text-xs">:</span>
                            <input
                              type="text"
                              placeholder="MM"
                              ref={endMinuteRef}
                              value={endMinute}
                              onChange={(e) => handleTimeInputChange(e, setEndMinute, 2, undefined, false, true)}
                              className="w-12 rounded-lg border border-[#CBD5E1] px-2 py-1 text-center text-xs focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            />
                            <select
                              value={endPeriod}
                              onChange={(e) => setEndPeriod(e.target.value as 'AM' | 'PM')}
                              className="rounded-lg border border-[#CBD5E1] px-2 py-1 text-xs focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            >
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      {showCustomTime && selectedDates.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {getDateRangeMessage() && (
                            <p className="text-sm text-[#AB6D0A]">{getDateRangeMessage()}</p>
                          )}
                          {isCustomTimeInPast() && (
                            <p className="text-sm text-red-600 font-medium">
                              ⚠️ This time has already passed today. Please select a future time.
                            </p>
                          )}
                          
                          {/* Shift Duration Validation */}
                          {startHour && startMinute && endHour && endMinute && (
                            (() => {
                              const validation = validateShiftDuration();
                              return !validation.isValid ? (
                                <p className="text-sm text-red-600 font-medium">
                                  ⚠️ {validation.message}
                                </p>
                              ) : null;
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                

                
                <div className="flex justify-between space-x-3">
                  <button
                    type="button"
                    onClick={() => setActiveStep(1)}
                    className="rounded-lg border border-[#CBD5E1] px-4 py-2 text-sm font-medium text-[#677D94] transition-colors hover:bg-[#F8FAFC]"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveStep(3)}
                    disabled={
                      !selectedTimeSlot ||
                      (showCustomTime && (!startHour || !startMinute || !endHour || !endMinute || isCustomTimeInPast())) ||
                      (selectedTimeSlot !== 'Custom Time' && isTimeSlotInPast(selectedTimeSlot)) ||
                      (showCustomTime && Boolean(startHour) && Boolean(startMinute) && Boolean(endHour) && Boolean(endMinute) && !isShiftDurationValid())
                    }
                    className="rounded-lg bg-[#4A6FA5] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#3A5F7D] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next: Shift Details
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Shift Details */}
            {activeStep === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-[#2A333C]">Shift Details</h3>
                  <p className="mt-1 text-sm text-[#677D94]">Configure the shift specifications</p>
                </div>
                

                
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="rounded-xl border border-[#CBD5E1] bg-white p-6 shadow-sm">
                      <div className="mb-4 flex items-center justify-between">
                        <h4 className="text-lg font-semibold text-[#2A333C]">Shift Configuration</h4>
                        <button
                          type="button"
                          onClick={handleClearConfiguration}
                          className="flex items-center space-x-1 rounded-lg border border-[#FF6B6B] px-3 py-1 text-sm font-medium text-[#FF6B6B] transition-colors hover:bg-[#FF6B6B] hover:text-white"
                        >
                          <TrashIcon className="h-4 w-4" />
                          <span>Clear</span>
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Specialty Selection */}
                        <div>
                          <label className="mb-2 block text-sm font-medium text-[#2A333C]">
                            <UserIcon className="mr-2 inline h-4 w-4" />
                            Positions <span className="text-[#C84648]">*</span>
                          </label>
                          {positionsLoading ? (
                            <div className="flex items-center space-x-2 rounded-lg border border-[#CBD5E1] bg-[#F8FAFC] px-3 py-2">
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#CBD5E1] border-t-[#4A6FA5]"></div>
                              <span className="text-sm text-[#677D94]">Loading positions...</span>
                            </div>
                          ) : (
                            <select
                              {...register('position_id', { required: 'Specialty is required' })}
                              className="w-full rounded-lg border border-[#CBD5E1] px-3 py-2 text-sm focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            >
                              <option value="">Select a position</option>
                              {positions.map((position) => (
                                <option key={position.position_id} value={position.position_id}>
                                  {position.position_name}
                                </option>
                              ))}
                            </select>
                          )}
                          {errors.position_id && (
                            <p className="mt-1 text-sm text-[#C84648]">{errors.position_id.message}</p>
                          )}
                        </div>

                        {/* Break Time */}
                        <div>
                          <label className="mb-2 block text-sm font-medium text-[#2A333C]">
                            <ClockIcon className="mr-2 inline h-4 w-4" />
                            Break Time
                          </label>
                          <select
                            {...register('break_time', { required: 'Break time is required', valueAsNumber: true })}
                            className="w-full rounded-lg border border-[#CBD5E1] px-3 py-2 text-sm focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                          >
                            <option value="">Select break time</option>
                            {breakTimeOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          {errors.break_time && (
                            <p className="mt-1 text-sm text-[#C84648]">{errors.break_time.message}</p>
                          )}
                        </div>

                        {/* Number of Openings */}
                        <div>
                          <label className="mb-2 block text-sm font-medium text-[#2A333C]">
                            <UserIcon className="mr-2 inline h-4 w-4" />
                            Number of Openings
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="7"
                            {...register('number_of_openings', { 
                              required: 'Number of openings is required', 
                              valueAsNumber: true, 
                            min: { value: 1, message: 'Minimum 1 opening' },
                            max: { value: 7, message: 'Maximum 7 openings allowed' }
                            })}
                            onBlur={handleOpeningsBlur}
                            className="w-full rounded-lg border border-[#CBD5E1] px-3 py-2 text-sm focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                          />
                          {errors.number_of_openings && (
                            <p className="mt-1 text-sm text-[#C84648]">{errors.number_of_openings.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="rounded-xl border border-[#CBD5E1] bg-white p-6 shadow-sm">
                      <h4 className="mb-4 text-lg font-semibold text-[#2A333C]">
                        <UserIcon className="mr-2 inline h-4 w-4" />
                        Shift Information
                      </h4>
                      
                      <div className="space-y-4">
                        {/* Description */}
                        <div>
                          <div className="mb-2 flex items-center justify-between">
                            <label className="block text-sm font-medium text-[#2A333C]">Description <span className="text-[#C84648]">*</span></label>
                            {facilityDetails?.description && (
                              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                
                              </span>
                            )}
                          </div>
                          <textarea
                            {...register('description', { required: 'Description is required' })}
                            className="w-full rounded-lg border border-[#CBD5E1] px-3 py-2 text-sm focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            rows={3}
                            maxLength={500}
                            placeholder="Enter shift description..."
                          />
                          <div className="mt-1 flex justify-between text-xs">
                            <span className="text-[#C84648]">{errors.description?.message}</span>
                            <span className="text-[#677D94]">{watch('description')?.length || 0}/500 characters</span>
                          </div>
                          {facilityDetails?.description && (
                            <div className="mt-1 text-xs text-[#f36a2a]">
                              Need to make changes? Head to the{' '}
                              <a 
                                href="/facility-info" 
                                className="font-medium underline hover:text-blue-800 transition-colors"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Facility Info page
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Instructions */}
                        <div>
                          <div className="mb-2 flex items-center justify-between">
                            <label className="text-sm font-medium text-[#2A333C]">Instructions <span className="text-[#C84648]">*</span></label>
                            {facilityDetails?.instructions && (
                              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                
                              </span>
                            )}
                          </div>
                          <textarea
                            {...register('instructions', { required: 'Instructions are required' })}
                            className="w-full rounded-lg border border-[#CBD5E1] px-3 py-2 text-sm focus:border-[#4A6FA5] focus:ring-1 focus:ring-[#4A6FA5]"
                            rows={3}
                            maxLength={500}
                            placeholder="Enter shift instructions..."
                          />
                          <div className="mt-1 flex justify-between text-xs">
                            <span className="text-[#C84648]">{errors.instructions?.message}</span>
                            <span className="text-[#677D94]">{watch('instructions')?.length || 0}/500 Characters</span>
                          </div>
                          {facilityDetails?.instructions && (
                            <div className="mt-1 text-xs text-[#f36a2a]">
                              Need to make changes? Head to the{' '}
                              <a 
                                href="/facility-info" 
                                className="font-medium underline hover:text-blue-800 transition-colors"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Facility Info page
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="rounded-xl border border-[#E0EDE2] bg-[#F8FAFC] p-4">
                  <h4 className="mb-3 text-sm font-semibold text-[#3A7D44]">Shift Summary</h4>
                  
                  {/* Basic Info Grid */}
                  <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4 mb-4">
                    <div>
                      <span className="text-[#677D94] text-xs">Dates:</span>
                      <p className="font-medium text-[#2A333C] text-sm">{selectedDates.length} selected</p>
                    </div>
                    <div>
                      <span className="text-[#677D94] text-xs">Time:</span>
                      <p className="font-medium text-[#2A333C] text-sm">{getFormattedTimeDisplay()}</p>
                    </div>
                    <div>
                      <span className="text-[#677D94] text-xs">Position:</span>
                      <p className="font-medium text-[#2A333C] text-sm">
                        {positions.find(p => p.position_id === watchedPositionId)?.position_name || 'Not selected'}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#677D94] text-xs">Openings:</span>
                      <p className="font-medium text-[#2A333C] text-sm">
                        {watch('number_of_openings') || 0}
                        <span className="text-[#677D94] text-xs ml-1">(1-7 allowed)</span>
                      </p>
                    </div>
                  </div>

                  {/* Selected Dates */}
                  {selectedDates.length > 0 && (
                    <div className="mb-4">
                      <span className="text-[#677D94] text-xs block mb-2">Selected Dates:</span>
                      <div className="flex flex-wrap gap-1">
                        {selectedDates.sort().map((date, index) => (
                          <span
                            key={date}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-[#E0EDE2] text-[#3A7D44] border border-[#3A7D44]/20"
                          >
                            {dayjs(date).format('MMM D')}
                            {index < selectedDates.length - 1 && (
                              <span className="ml-1 text-[#3A7D44]/60">•</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}


                </div>
                

                
                <div className="flex justify-between space-x-3">
                  <button
                    type="button"
                    onClick={() => setActiveStep(2)}
                    className="rounded-lg border border-[#CBD5E1] px-4 py-2 text-sm font-medium text-[#677D94] transition-colors hover:bg-[#F8FAFC]"
                  >
                    Back
                  </button>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="rounded-lg border border-[#CBD5E1] px-4 py-2 text-sm font-medium text-[#677D94] transition-colors hover:bg-[#F8FAFC]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!isFormSubmittable || isSubmitting}
                      className="flex items-center space-x-2 rounded-lg bg-[#4A6FA5] px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-[#3A5F7D] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <PlusIcon className="h-4 w-4" />
                          <span>Create Shift</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    );
  };

  export default PostShiftModal;